// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import type { ReactNode } from 'react';
import type { Editor } from '@tiptap/react';
import { InputLabel } from '@loa/ui-components';
import { FormInput } from '../FormInput';
import { TiptapEditor, EditorToolbar } from '../invitation/TiptapEditor';

export type EmailComposerPanelProps = Readonly<{
  /** Content rendered above the form fields (e.g. client info + policies table). */
  children?: ReactNode;

  /** Subject line state. */
  subject: string;
  onSubjectChange: (value: string) => void;
  subjectPlaceholder?: string;

  /** Recipients read-only field. Pass {@code undefined} to hide it. */
  recipients?: string;
  recipientsPlaceholder?: string;

  /** Rich-text body state. */
  body: string;
  onBodyChange: (html: string) => void;

  /** Whether the body/subject was modified from the default (drives Reset button). */
  isModified: boolean;
  onReset: () => void;

  /** Footer actions. */
  onCancel: () => void;
  onSend: () => void;
  sendLabel?: string;
  sending?: boolean;
  sendDisabled?: boolean;

  /** Optional error banner shown above the footer. */
  error?: string | null;

  /**
   * Optional content rendered BELOW the Send/Cancel footer.
   * The Nudge Provider page uses this slot for the nudge-history table —
   * history reads naturally after the email the advisor just composed,
   * not above it. {@link children} still covers the above-composer slots
   * (recipient info, policies card, rate-limit warnings).
   */
  footer?: ReactNode;

  /** Forwarded to every input when {@code true}. */
  disabled?: boolean;
}>;

/**
 * Shared email composition scaffold used by the Relationship "Send Invitations"
 * flow and the Change of Agency wizard step 2. Renders the subject field,
 * optional read-only recipients field, a rich-text body editor card (toolbar +
 * Reset To Default button), an optional error banner, and Cancel/Send footer
 * buttons. The {@code children} prop lets callers inject content above the form
 * (client header, policies table, etc.).
 */
export function EmailComposerPanel({
  children,
  subject,
  onSubjectChange,
  subjectPlaceholder,
  recipients,
  recipientsPlaceholder,
  body,
  onBodyChange,
  isModified,
  onReset,
  onCancel,
  onSend,
  sendLabel = 'Send Email',
  sending = false,
  sendDisabled = false,
  error,
  footer,
  disabled = false,
}: EmailComposerPanelProps) {
  const [editor, setEditor] = useState<Editor | null>(null);
  const isDisabled = disabled || sending;
  const showRecipients = recipients !== undefined;

  return (
    <div className="w-full bg-page-bg flex-1 flex flex-col">
      {children}

      {/* Subject + recipients */}
      <div className="p-lg gap-lg flex-col flex bg-page-bg">
        <div className="flex flex-col">
          <InputLabel>Subject line</InputLabel>
          <FormInput
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            disabled={isDisabled}
            maxLength={200}
            placeholder={subjectPlaceholder}
            data-testid="subject-input"
          />
          <div className="text-right text-xs text-grey-02 mt-1">
            {subject.length}/200
          </div>
        </div>
        {showRecipients && (
          <div className="flex flex-col">
            <InputLabel>Recipients</InputLabel>
            <FormInput
              value={recipients ?? ''}
              disabled
              placeholder={recipientsPlaceholder}
              variant="light"
            />
          </div>
        )}
      </div>

      {error && (
        <div
          className="mx-lg mb-md bg-red-50 border border-red-200 rounded-lg p-3"
          data-testid="send-result"
          role="alert"
          aria-live="polite"
        >
          <p className="text-sm font-semibold text-red-700 !m-0">Error</p>
          <p className="text-sm text-red-700 !m-0">{error}</p>
        </div>
      )}

      {/* Rich text editor card */}
      <div className="bg-page-bg rounded-card shadow-[0_0_10px_0_rgba(0,0,0,0.10)] mx-lg mb-lg">
        <div className="flex items-center justify-between bg-lilac-06 rounded-t-[16px] p-md">
          <div className="flex">
            <EditorToolbar editor={editor} disabled={isDisabled} />
          </div>
          <div className="flex">
            <button
              type="button"
              onClick={onReset}
              disabled={isDisabled || !isModified}
              data-testid="reset-button"
              className="h-[32px] px-md border border-purple-primary bg-lilac-06 rounded-lg flex items-center gap-sm hover:bg-lilac-05 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.8334 6.9974C12.8334 10.2174 10.22 12.8307 7.00002 12.8307C3.78002 12.8307 1.81419 9.5874 1.81419 9.5874M1.81419 9.5874H4.45085M1.81419 9.5874V12.5041M1.16669 6.9974C1.16669 3.7774 3.75669 1.16406 7.00002 1.16406C10.8909 1.16406 12.8334 4.4074 12.8334 4.4074M12.8334 4.4074V1.49073M12.8334 4.4074H10.2434" stroke="#2D0A6C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="text-purple-primary font-sans text-body-03 font-medium capitalize cursor-pointer">Reset To Default</span>
            </button>
          </div>
        </div>

        <TiptapEditor
          content={body}
          onChange={onBodyChange}
          placeholder="Compose your email..."
          disabled={isDisabled}
          minHeight="300px"
          maxHeight="500px"
          ariaLabel="Email body editor"
          hideToolbar
          onEditorReady={setEditor}
        />
      </div>

      {/* Footer */}
      <div className="flex justify-end mr-lg gap-md pb-lg">
        <button
          type="button"
          onClick={onCancel}
          disabled={isDisabled}
          data-testid="cancel-button"
          className="flex h-[48px] py-[10px] px-xl justify-center items-center gap-sm rounded-button border border-purple-primary text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize disabled:opacity-60 hover:bg-lilac-05 transition-colors cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSend}
          disabled={isDisabled || sendDisabled}
          data-testid="resend-submit"
          className="flex h-[48px] py-[10px] px-xl justify-center items-center gap-sm rounded-button bg-purple-primary text-white font-sans text-body-02 font-medium leading-normal capitalize disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#220852] transition-colors cursor-pointer"
        >
          {sending ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
          ) : (
            sendLabel
          )}
        </button>
      </div>

      {footer}
    </div>
  );
}

export default EmailComposerPanel;
